import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Printer } from "lucide-react";
import { api, auth } from "@/lib/api";

/**
 * KitchenPoster — a print-ready, A4-portrait poster you can stick on the
 * fridge or kitchen iPad. Whoever scans the QR lands directly on the join
 * screen for this family — they'll still need the family PIN to enter,
 * which the poster intentionally does NOT include (PIN is a different
 * trust level than "I'm in this kitchen").
 *
 * The poster is a separate route (/family/poster) instead of a print
 * dialog inside Settings because:
 *   1. Browsers print whatever fills the viewport — easier to hand the
 *      whole page over to print() than to clip a modal.
 *   2. Lets people open it on a desktop, hit Cmd-P, "Save as PDF" and
 *      send it to someone else's printer (a babysitter, grandparent).
 *
 * Print stylesheet hides the toolbar and forces A4 portrait.
 */
export default function KitchenPoster() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    // Short-circuit when there's no token at all — otherwise the
    // settings GET hangs on a 401 retry and the page sits at "Loading".
    if (!auth.getToken()) {
      setLoading(false);
      return () => { alive = false; };
    }
    api.getFamilySettings()
      .then((s) => alive && setSettings(s))
      .catch(() => {/* will fall back to error state below */})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const onPrint = () => {
    window.print();
  };

  const joinUrl = settings?.join_code
    ? `${window.location.origin}/?join=${encodeURIComponent(settings.join_code)}`
    : "";
  const familyName = settings?.family_name || settings?.name || "Our family";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg)]">
        <div className="label-tiny text-[color:var(--ink-3)]">Loading poster…</div>
      </div>
    );
  }

  if (!settings?.join_code) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--bg)] px-6">
        <div className="max-w-md text-center" data-testid="poster-no-family">
          <h1 className="font-display-italic text-3xl mb-3">No family yet</h1>
          <p className="text-[color:var(--ink-2)] mb-5">
            Open FamFeel and create or join a family first — then come back here
            to print the kitchen poster.
          </p>
          <Link to="/app" className="ff-btn ff-btn-primary">
            Open FamFeel
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--bg)]" data-testid="kitchen-poster-page">
      {/* Toolbar — hidden when printing */}
      <div className="ff-no-print sticky top-0 z-10 bg-[color:var(--bg)] border-b border-[color:var(--line-soft)] px-5 py-3 flex items-center gap-3">
        <Link
          to="/app"
          className="ff-btn"
          data-testid="poster-back"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2} />
          Back
        </Link>
        <div className="label-tiny text-[color:var(--ink-3)] flex-1">
          One page · A4 portrait · save as PDF in the print dialog to email it
        </div>
        <button
          type="button"
          onClick={onPrint}
          className="ff-btn ff-btn-primary"
          data-testid="poster-print"
        >
          <Printer className="w-4 h-4" strokeWidth={2} />
          Print
        </button>
      </div>

      {/* Sheet — sized as a real A4 portrait page on screen too */}
      <div className="flex justify-center px-4 py-8 ff-print-area">
        <div
          className="ff-poster-sheet bg-white shadow-md rounded-2xl overflow-hidden"
          data-testid="poster-sheet"
        >
          <div className="ff-poster-inner">
            <div className="ff-poster-eyebrow">FamFeel · Kitchen poster</div>
            <h1 className="ff-poster-title">
              Welcome to {familyName}.
            </h1>
            <p className="ff-poster-sub">
              Scan, pick your avatar, type the family PIN.
              <br />
              That's it — you're in.
            </p>

            <div className="ff-poster-qr-wrap">
              <div className="ff-poster-qr">
                <QRCodeSVG
                  value={joinUrl}
                  size={520}
                  level="M"
                  bgColor="#FFFFFF"
                  fgColor="#1E2A44"
                />
              </div>
              <div className="ff-poster-code">
                <div className="ff-poster-code-label">Or type this code</div>
                <div className="ff-poster-code-value">{settings.join_code}</div>
                <div className="ff-poster-code-host">famfeel.app</div>
              </div>
            </div>

            <ol className="ff-poster-steps">
              <li>
                <span className="ff-poster-step-num">1</span>
                <span>
                  <strong>Scan</strong> with the camera on any phone or tablet.
                </span>
              </li>
              <li>
                <span className="ff-poster-step-num">2</span>
                <span>
                  <strong>Pick your avatar</strong> — your face is your login.
                </span>
              </li>
              <li>
                <span className="ff-poster-step-num">3</span>
                <span>
                  <strong>Type the family PIN</strong> (ask whoever set this up).
                </span>
              </li>
            </ol>

            <div className="ff-poster-foot">
              <div>
                A calm, shared family dashboard.<br />
                No accounts to remember. No ads. No tracking.
              </div>
              <div className="ff-poster-foot-brand">
                FAMFEEL.APP
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
